import { Box, Modal, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Esercizio, THead, tooltipColumnIds } from "../interfaces/Esercizio";
//import ExCodeTable from "./ExCodeTable ";
import { kMaxLength } from "buffer";
import ODMTable from "./ODMTable";

const style = {
    position: "absolute" as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '12px',
    p: 4,
};

interface ModalPosizioneFinalitaProps {
    //token: string;
    open: boolean;
    onClose: () => void;
    esercizio: Esercizio;
    positionChange: (e: { value: string }) => void;
    purposeChange: (e: { value: string }) => void;
}

const getPattern = (purpose: string | null): RegExp => {
    switch (purpose) {
        case "Rebalancing": return /^\d{4}(-[A-Z])?$/;
        case "Control": return /^\d{8}(-[A-Z])?$/;
        case "PreparatoryToFunction": return /^\d{6}(-[A-Z])?$/;
        default: return /^.*$/;
    }
};

const getMaxLength = (purpose: string | null): number => {
    switch (purpose) {
        case "Rebalancing": return 6; // 4 numeri + opzionale -X
        case "Control": return 10;     // 8 numeri + opzionale -X
        case "PreparatoryToFunction": return 8; // 6 numeri + opzionale -X
        default: return 50;
    }
};

const getPlaceholder = (purpose: string | null): string => {
    switch (purpose) {
        case "Rebalancing": return "es. 0010 o 0010-A";
        case "Control": return "es. 00000010 o 00000010-A";
        case "PreparatoryToFunction": return "es. 000010 o 000010-A";
        default: return "Digita codice";
    }
};

const purposeMap: Record<string, string> = {
    "Riequilibrio": "Rebalancing",
    "Controllo": "Control",
    "Propedeutico alla funzione": "PreparatoryToFunction"
};

const ModalPosizioneFinalita = ({ open, onClose, esercizio, positionChange, purposeChange }: ModalPosizioneFinalitaProps) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPurpose, setSelectedPurpose] = useState<string>(esercizio.purpose ? esercizio.purpose : "Rebalancing");
    const [posizione, setPosizione] = useState<string>(esercizio.position ?? "");
    const [lista, setLista] = useState<any[]>([]);

    const isValid = getPattern(selectedPurpose).test(posizione);
    const isDuplicate = lista.some(item =>
        item.position === posizione);
    const isPurposeSelected = selectedPurpose !== null;

    const datiEsercizi = [
        { id: "frequency", label: "Frequenza d'utilizzo" },
        { id: "position", label: "Posizione" },
        { id: "full_name", label: "Nome" },
        { id: "gravity_management_complexity", label: "C.C.G" },
        { id: "motor_coordination_complexity", label: "C.C.M" },
        { id: "cardiorespiratory_complexity", label: "I.C.R" },
        { id: "muscle_groups", label: "Comparto" },
        { id: "equipment", label: "Attrezzi" },
        { id: "posture", label: "Assetto" },
        { id: "", label: "Modalità" },
        { id: "", label: "Finalità" },
        { id: "", label: "Funzionalità Complessa" },
        { id: "", label: "Raccomandato per" },
    ];

    const fetchActivities = async () => {
        try {
            const headers = new Headers();
            //if (token) {
            //    headers.set('Authorization', `Token ${token}`);
            //}
            headers.set('Accept', 'application/json');
            const requestInit: RequestInit = {
                method: 'GET',
                headers: headers,
                credentials: 'include',
            }
            const response = await fetch('/api/v0/activities', requestInit);
            if (!response.ok) throw new Error('Errore nel recupero delle attività');
            const data = await response.json();
            setLista(data);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = () => {
        if (!isValid || isDuplicate) return;
        positionChange({ value: posizione });
        if (selectedPurpose) {
            purposeChange({ value: selectedPurpose });
        }
        onClose();
    };

    const handlePurpose = (value: string) => {
        setSelectedPurpose(value);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value.toUpperCase();
        const filtered = rawValue.replace(/[^A-Z0-9\-]/g, "");
        const maxLength = getMaxLength(selectedPurpose);
        setPosizione(filtered.slice(0, maxLength));
    };

    useEffect(() => {
        if(open && lista.length==0){
            fetchActivities();
        }
    }, [open]);

    useEffect(() => {
        setSelectedPurpose(esercizio.purpose ? esercizio.purpose : "Rebalancing");
        setPosizione(esercizio.position ?? "");
    }, [esercizio]);


    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={style}>
                <h3 className="text-2xl font-semibold mb-6">Inserisci codice esercizio</h3>
                <div className="row justify-content-between mb-3">
                    <div className="col">
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder={getPlaceholder(selectedPurpose)}
                            value={posizione}
                            onChange={handleInputChange}
                            error={!isValid || isDuplicate || !isPurposeSelected || !!error}
                            helperText={
                                error ||
                                (!isValid && posizione
                                    ? 'Formato non valido per la finalità selezionata'
                                    : isDuplicate
                                        ? 'Questo codice è già stato utilizzato'
                                        : !isPurposeSelected
                                            ? 'Seleziona una finalità'
                                            : ' ')
                            }
                            size="small"
                            inputProps={{ maxLength: getMaxLength(selectedPurpose) }}
                        />
                    </div>
                    <div className="col text-end">
                        <button
                            className={`btn ms-2 ${selectedPurpose === "Rebalancing" ? "btn-dark" : "btn-light"}`}
                            onClick={() => handlePurpose("Rebalancing")}
                        >
                            Riequilibrio
                        </button>
                        <button
                            className={`btn ms-2 ${selectedPurpose === "Control" ? "btn-dark" : "btn-light"}`}
                            onClick={() => handlePurpose("Control")}
                        >
                            Controllo
                        </button>
                        <button
                            className={`btn ms-2 ${selectedPurpose === "PreparatoryToFunction" ? "btn-dark" : "btn-light"}`}
                            onClick={() => handlePurpose("PreparatoryToFunction")}
                        >
                            Propedeutico alla funzione
                        </button>
                        {!isPurposeSelected && (
                            <div className="text-danger mt-2">Seleziona una finalità</div>
                        )}
                    </div>
                </div>
                <ODMTable
                    columns={THead}
                    rows={lista.filter(item => purposeMap[item.purpose] === selectedPurpose)}
                    navTable="esercizi"
                    onRowClick={() => null}
                    stickyColumnIds={["position", "full_name",]}
                    edit={true}
                    heightTable="50vh"
                    tooltipColumnIds={tooltipColumnIds}
                />
                <div className="mt-3 flex justify-end gap-4">
                    <button className="btn btn-light" onClick={onClose}>Annulla</button>
                    <button className="btn btn-dark" disabled={!isValid || !isPurposeSelected} onClick={handleSave}>Salva</button>
                </div>
            </Box>
        </Modal>
    );
};

export default ModalPosizioneFinalita;
